/**
 * InBodySettingsCard.jsx
 * Admin settings card for InBody integration, visibility, and body composition
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Scan, Eye, EyeOff, RefreshCw, Info, Smartphone, Globe, BarChart3, Scale } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const InBodySettingsCard = () => {
  const [inbodyVisible, setInbodyVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inbody/settings/portal-visibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInbodyVisible(response.data.visible || false);
    } catch (error) {
      console.error('Error loading InBody settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (newValue) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/inbody/settings/portal-visibility/${newValue}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInbodyVisible(newValue);
      toast.success(newValue ? 'InBody für Kunden aktiviert' : 'InBody für Kunden deaktiviert');
    } catch (error) {
      console.error('Error toggling InBody visibility:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center items-center">
          <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Kundenportal-Sichtbarkeit */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Scan className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                InBody Körperanalyse
                <Badge variant={inbodyVisible ? "default" : "secondary"} className={inbodyVisible ? "bg-green-500" : ""}>
                  {inbodyVisible ? 'Aktiv' : 'Deaktiviert'}
                </Badge>
              </CardTitle>
              <CardDescription className="mt-1">
                Körperanalyse-Daten für Kunden sichtbar machen
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-3">
              {inbodyVisible ? (
                <Eye className="w-5 h-5 text-green-500" />
              ) : (
                <EyeOff className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <p className="font-medium">InBody für Kunden anzeigen</p>
                <p className="text-gray-500 text-sm">
                  Wenn aktiviert, sehen Kunden ihre InBody-Messungen im Portal und in der App
                </p>
              </div>
            </div>
            <Switch 
              checked={inbodyVisible}
              onCheckedChange={handleToggle}
              disabled={saving}
              data-testid="inbody-visibility-toggle"
            />
          </div>

          {/* Where it appears */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-600">Sichtbar in:</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className={`p-3 rounded-lg border ${inbodyVisible ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Globe className={`w-4 h-4 ${inbodyVisible ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${inbodyVisible ? 'text-green-700' : 'text-gray-500'}`}>
                    Kundenportal
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Gesundheit → Körper Tab
                </p>
              </div>
              <div className={`p-3 rounded-lg border ${inbodyVisible ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Smartphone className={`w-4 h-4 ${inbodyVisible ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${inbodyVisible ? 'text-green-700' : 'text-gray-500'}`}>
                    Mobile App
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Körper → InBody Tab
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* InBody Integration */}
      <Card className="border-dashed border-2 border-gray-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-gray-400" />
            InBody Integration
            <Badge variant="outline" className="ml-2">Vorbereitet</Badge>
          </CardTitle>
          <CardDescription>
            Das System ist bereit für die automatische InBody-Datenintegration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Verfügbare InBody-Daten (wenn aktiviert):</strong>
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Körperfettanteil (%)</li>
              <li>• Muskelmasse (kg)</li>
              <li>• Viszerales Fett</li>
              <li>• Körperwasser (%)</li>
              <li>• BMR (gemessen)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Manual Body Tracking Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Scale className="w-5 h-5 text-green-600" />
            Manuelles Körper-Tracking
          </CardTitle>
          <CardDescription>
            Bis InBody aktiv ist, werden manuelle Körper-Tracking Daten verwendet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700">
              <strong>Aktive Datenquellen:</strong>
            </p>
            <ul className="text-sm text-green-600 mt-2 space-y-1">
              <li>✓ Manuell eingetragenes Körpergewicht</li>
              <li>✓ Manuell eingetragene Körpermasse</li>
              <li>✓ Körpergrösse aus dem Profil</li>
              <li>✓ Geschlecht und Alter für BMR-Berechnung</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="flex gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700">
          <p className="font-medium text-blue-700 mb-1">Hinweis</p>
          <p>
            InBody-Messungen werden bei Ihnen im Studio mit dem InBody-Gerät durchgeführt. 
            Die Daten werden automatisch mit dem Kundenprofil verknüpft, wenn die E-Mail-Adresse übereinstimmt.
          </p>
          <p className="mt-2">
            Sie können auch Demo-Daten für Testzwecke in der Kunden-Detailansicht generieren.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InBodySettingsCard;
